import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { CircularProgress } from '@mui/material';

/**
 * A circular gauge component that visually displays a score with enhanced styling
 * @param {number} value - Current score value
 * @param {number} max - Maximum possible score (defaults to 100)
 * @param {number} size - Size of the gauge in pixels
 * @param {number} thickness - Thickness of the gauge
 * @param {string} color - MUI color theme to use ('primary', 'secondary', etc.)
 * @param {boolean} showLabel - Whether to show the label
 * @param {number} labelFontSize - Font size of the label
 * @param {boolean} withShadow - Whether to add shadow effects
 * @param {Object} sx - Additional styles to apply
 */
const ScoreGauge = ({ 
  value = 0, 
  max = 100, 
  size = 120, 
  thickness = 6, 
  color = 'primary',
  showLabel = true,
  labelFontSize = 24,
  withShadow = true,
  sx = {}
}) => {
  const theme = useTheme();
  
  // Calculate percentage
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  // Determine color based on value
  let colorValue;
  if (typeof color === 'string') {
    switch (color) {
      case 'success':
        colorValue = theme.palette.success.main;
        break;
      case 'warning':
        colorValue = theme.palette.warning.main;
        break;
      case 'error':
        colorValue = theme.palette.error.main;
        break;
      case 'primary':
      default:
        colorValue = theme.palette.primary.main;
    }
  } else {
    colorValue = color;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translateZ(0)',
        '&:hover': withShadow ? {
          transform: 'scale(1.05) translateZ(0)'
        } : {},
        ...sx
      }}
    >
      {/* Background circle */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          color: theme.palette.grey[200],
          position: 'absolute',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      
      {/* Progress circle */}
      <CircularProgress
        variant="determinate"
        value={percentage}
        size={size}
        thickness={thickness}
        sx={{
          color: colorValue,
          position: 'relative',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
            transition: 'stroke-dashoffset 1.2s ease-in-out',
            boxShadow: withShadow ? `0 0 10px ${colorValue}40` : 'none',
            filter: withShadow ? `drop-shadow(0 0 2px ${colorValue}80)` : 'none'
          }
        }}
      />
      
      {/* Inner shadow for depth */}
      {withShadow && (
        <Box
          sx={{
            position: 'absolute',
            width: size - thickness * 3,
            height: size - thickness * 3,
            borderRadius: '50%',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
            background: 'transparent'
          }}
        />
      )}
      
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          transition: 'all 0.3s ease'
        }}
      >
        {showLabel && (
          <>
            <Typography
              variant="h4"
              component="div"
              color="text.primary"
              fontWeight="bold"
              sx={{ 
                fontSize: labelFontSize,
                lineHeight: 1,
                transition: 'all 0.2s ease',
                textShadow: withShadow ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{ 
                lineHeight: 1, 
                mt: 0.5,
                fontSize: labelFontSize * 0.5,
                opacity: 0.7
              }}
            >
              /{max}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ScoreGauge;